import React, { useMemo } from 'react';

import { isSSR } from '@atlaskit/editor-common/core-utils';
import { ToolbarSize } from '@atlaskit/editor-common/types';
import { componentWithCondition } from '@atlaskit/platform-feature-flags-react/component-with-condition';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { WidthObserver } from '@atlaskit/width-detector/width-observer';

import { isFullPage } from '../../utils/is-full-page';

import { useElementWidth } from './hooks';
import { Toolbar } from './Toolbar';
import { toolbarSizeToWidth, widthToToolbarSize } from './toolbar-size';
import type { ToolbarWithSizeDetectorProps } from './toolbar-types';
import { ToolbarSizeDetectorWrapperCompiled } from './ToolbarSizeDetectorWrapper-compiled';
import { ToolbarSizeDetectorWrapperEmotion } from './ToolbarSizeDetectorWrapper-emotion';

const ToolbarSizeDetectorWrapperMigration = componentWithCondition(
	() => expValEquals('platform_editor_core_non_ecc_static_css', 'isEnabled', true),
	ToolbarSizeDetectorWrapperCompiled,
	ToolbarSizeDetectorWrapperEmotion,
);

export const ToolbarWithSizeDetector = (props: ToolbarWithSizeDetectorProps): React.JSX.Element => {
	const ref = React.useRef<HTMLDivElement>(null);
	const [width, setWidth] = React.useState<number | undefined>(undefined);
	const elementWidth = useElementWidth(ref, {
		skip: typeof width !== 'undefined',
	});

	const defaultToolbarSize = isSSR() && isFullPage(props.appearance) ? ToolbarSize.XXL : undefined;
	const toolbarSize =
		typeof width === 'undefined' && typeof elementWidth === 'undefined'
			? defaultToolbarSize
			: // Ignored via go/ees005
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				widthToToolbarSize((width || elementWidth)!, props.appearance);

	const minWidthValue = useMemo<string>((): string => {
		if (props.hasMinWidth) {
			const toolbarWidth =
				isFullPage(props.appearance) && props.twoLineEditorToolbar ? ToolbarSize.S : ToolbarSize.M;
			return `${toolbarSizeToWidth(toolbarWidth, props.appearance)}px`;
		} else {
			const isPreviewPanelResponsivenessEnabled = editorExperiment(
				'platform_editor_preview_panel_responsiveness',
				true,
				{
					exposure: true,
				},
			);
			return isPreviewPanelResponsivenessEnabled ? 'fit-content' : '254px';
		}
	}, [props.appearance, props.hasMinWidth, props.twoLineEditorToolbar]);

	const memoizedWrapperStyle = useMemo(() => ({ minWidth: minWidthValue }), [minWidthValue]);
	const wrapperStyle = expValEquals('platform_editor_core_non_ecc_static_css', 'isEnabled', true)
		? memoizedWrapperStyle
		: { minWidth: minWidthValue };

	return (
		<ToolbarSizeDetectorWrapperMigration
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- minWidth is computed dynamically from props.hasMinWidth, props.appearance, props.twoLineEditorToolbar, and the platform_editor_preview_panel_responsiveness experiment, so it cannot be expressed as a static css value.
			style={wrapperStyle}
		>
			<WidthObserver setWidth={setWidth} />
			{props.editorView && toolbarSize ? (
				<Toolbar
					toolbarSize={toolbarSize}
					items={props.items}
					editorView={props.editorView}
					editorActions={props.editorActions}
					eventDispatcher={props.eventDispatcher}
					providerFactory={props.providerFactory}
					appearance={props.appearance}
					popupsMountPoint={props.popupsMountPoint}
					popupsBoundariesElement={props.popupsBoundariesElement}
					popupsScrollableElement={props.popupsScrollableElement}
					disabled={props.disabled}
					dispatchAnalyticsEvent={props.dispatchAnalyticsEvent}
					containerElement={props.containerElement}
					hasMinWidth={props.hasMinWidth}
					twoLineEditorToolbar={props.twoLineEditorToolbar}
				/>
			) : (
				<div ref={ref} />
			)}
		</ToolbarSizeDetectorWrapperMigration>
	);
};
